import React, { FC, useMemo, useState, useEffect } from 'react';
import { Edit, Undo } from '@mui/icons-material';
import { InputAdornment, TextField, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { useUpdateNetworkAffiliateCodeMutation } from 'reduxState/store/applicationManagement/api';
import {
  ApplicationManagement,
  NetworkAffiliateCode,
  UpdateNetworkAffiliateCodeErrorData,
} from '../../../reduxState/store/applicationManagement/types';
import { ConfirmEdit } from './ConfirmEdit';
import '../scss/AppName.scss';

const CJ_NETWORK_ID = 14;

export interface CjPidProps {
  applicationId: string;
  applicationManagement: ApplicationManagement | null;
}

const extractNetworkId = (networkAffiliateCode: NetworkAffiliateCode): number | null => {
  const rawNetworkId =
    networkAffiliateCode.network_id ??
    networkAffiliateCode.NetworkID ??
    networkAffiliateCode.networkId ??
    networkAffiliateCode.NetworkId;

  if (rawNetworkId === undefined || rawNetworkId === null) return null;

  const parsedNetworkId = Number(rawNetworkId);
  return Number.isNaN(parsedNetworkId) ? null : parsedNetworkId;
};

const extractPidValues = (networkAffiliateCode: NetworkAffiliateCode): string[] => {
  const rawPidValue =
    networkAffiliateCode.network_affiliate_code ??
    networkAffiliateCode.NetworkAffiliateCode ??
    networkAffiliateCode.networkAffiliateCode ??
    networkAffiliateCode.affiliate_code ??
    networkAffiliateCode.AffiliateCode ??
    networkAffiliateCode.network_affiliate_codes ??
    networkAffiliateCode.NetworkAffiliateCodes ??
    networkAffiliateCode.networkAffiliateCodes ??
    networkAffiliateCode.pid ??
    networkAffiliateCode.PID ??
    networkAffiliateCode.Pid ??
    '';

  if (Array.isArray(rawPidValue)) {
    return normalizePidValues(rawPidValue.join(','));
  }

  return normalizePidValues(String(rawPidValue));
};

const normalizePidValues = (value: string): string[] => {
  return Array.from(
    new Set(
      value
        .split(/[,\n]+/)
        .map(pidValue => pidValue.trim())
        .filter(Boolean),
    ),
  );
};

export const CjPid: FC<React.PropsWithChildren<CjPidProps>> = ({ applicationId, applicationManagement }) => {
  const cjNetworkAffiliateCodes = useMemo(() => {
    const networkAffiliateCodes = applicationManagement?.NetworkAffiliateCodes ?? [];
    return networkAffiliateCodes.filter(networkAffiliateCode => extractNetworkId(networkAffiliateCode) === CJ_NETWORK_ID);
  }, [applicationManagement]);

  const initialPidValues = useMemo(() => {
    const mergedPidValues = cjNetworkAffiliateCodes.flatMap(networkAffiliateCode => {
      return extractPidValues(networkAffiliateCode);
    });

    if (!mergedPidValues.length && applicationManagement?.CJPID) {
      return normalizePidValues(applicationManagement.CJPID);
    }

    return Array.from(new Set(mergedPidValues));
  }, [applicationManagement?.CJPID, cjNetworkAffiliateCodes]);

  const hasMultipleInitialPids = initialPidValues.length > 1;
  const initialPidValue = initialPidValues[0] ?? '';

  const [isDisabled, setDisabled] = useState(true);
  const [value, setValue] = useState(initialPidValue);
  const normalizedDisplayValue = normalizePidValues(value)[0] ?? value.trim();
  const [updateNetworkAffiliateCode, { isLoading }] = useUpdateNetworkAffiliateCodeMutation();

  useEffect(() => {
    setValue(initialPidValue);
  }, [initialPidValue]);

  if (!cjNetworkAffiliateCodes.length) return null;

  const toggleDisabled = (): void => setDisabled(prevState => !prevState);

  const cancelEdit = (): void => {
    setValue(initialPidValue);
    setDisabled(true);
  };

  const handleSave = async (): Promise<void> => {
    const normalizedPidValues = normalizePidValues(value);
    const originalPidValue = normalizePidValues(initialPidValue)[0] ?? '';
    if (hasMultipleInitialPids) {
      toast.error('Multiple existing CJ PIDs detected. Please contact engineering to consolidate them before editing.');
      return;
    }

    if (normalizedPidValues.length !== 1) {
      toast.error('Please provide exactly one CJ PID value.');
      return;
    }

    if (normalizedPidValues[0] === originalPidValue) {
      toast.info('No CJ PID changes to save.');
      setDisabled(true);
      return;
    }

    try {
      await updateNetworkAffiliateCode({
        appId: applicationId,
        networkId: CJ_NETWORK_ID,
        affiliate_code: normalizedPidValues[0],
      }).unwrap();
      setValue(normalizedPidValues[0]);
      toast.success('Associated CJ PIDs updated successfully');
      setDisabled(true);
    } catch (error: unknown) {
      let errorMessage = 'Failed to update associated CJ PIDs. Please try again.';
      let statusText = '';

      if (error && typeof error === 'object') {
        const errorWithStatus = error as {
          status?: string | number;
          data?: UpdateNetworkAffiliateCodeErrorData | string;
          error?: string;
        };
        if (errorWithStatus.status !== undefined) {
          statusText = String(errorWithStatus.status);
        }

        let parsedErrorData: UpdateNetworkAffiliateCodeErrorData | null = null;
        const { data } = errorWithStatus;
        if (data && typeof data === 'object') {
          parsedErrorData = data as UpdateNetworkAffiliateCodeErrorData;
        } else if (typeof data === 'string') {
          try {
            parsedErrorData = JSON.parse(data) as UpdateNetworkAffiliateCodeErrorData;
          } catch {
            // Keep default error when the backend responds with non-JSON text.
          }
        }

        if (parsedErrorData?.message) errorMessage = `Failed to update associated CJ PIDs: ${parsedErrorData.message}`;
        if (parsedErrorData?.ErrorMessage) errorMessage = `Failed to update associated CJ PIDs: ${parsedErrorData.ErrorMessage}`;
        if (!errorMessage.includes(':') && errorWithStatus.error) {
          errorMessage = `Failed to update associated CJ PIDs: ${errorWithStatus.error}`;
        }
      }

      if (statusText === '401') {
        errorMessage = 'Failed to update associated CJ PIDs: unauthorized. Your account may not have access to update this endpoint.';
      } else if (!errorMessage.includes(':') && statusText) {
        errorMessage = `Failed to update associated CJ PIDs (status ${statusText}). Please try again.`;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <div className="application-name settings-form-group ">
      <TextField
        id="cjPid"
        className={`application-name-input ${!isDisabled ? 'user-editing' : ''}`}
        label="Associated CJ PIDs"
        value={value}
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        inputRef={input => input && !isDisabled && input.focus()}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {!isDisabled ? (
                <Tooltip title="Cancel editing" role="tooltip">
                  <Undo
                    aria-label="Cancel editing associated CJ PID"
                    onClick={cancelEdit}
                    className="edit-icon-undo"
                    data-testid="undo-button"
                  />
                </Tooltip>
              ) : (
                <Edit
                  aria-label="Edit associated CJ PID"
                  onClick={toggleDisabled}
                  className="edit-icon"
                  data-testid="edit-button"
                />
              )}
            </InputAdornment>
          ),
        }}
        onChange={event => setValue(event.currentTarget.value)}
        disabled={isDisabled || isLoading}
        data-testid="cj-pid-textbox"
        helperText={
          hasMultipleInitialPids ? 'Multiple existing CJ PIDs detected. Please consolidate to one value before editing.' : ''
        }
      />
      {!isDisabled && (
        <ConfirmEdit
          headerText={'Edit Associated CJ PIDs'}
          confirmText={`Confirm save to update associated CJ PIDs:`}
          changeText={normalizedDisplayValue}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
